import React from "react";
import UpToInternalUsers from "../Components/UpToInternalUsers";
import SupportQuestionMark from "../../supportbeacon/SupportQuestionMark";
import SaveByPayingPerYear from "../Components/SaveByPayingPerYear";
import { NextIsFree, NextIsReadOnly, NextIsCancelled, NotImplementedSubscription, TermsRow } from "../Components/CommonPanelContent";
import moment from "moment";
import { findEnumLabel } from "utilities/enumutils";
import { formatLongDate, formatShortDate } from "utilities";

interface Props {
  activeSubscription: server.dto.Subscription
  nextSubscriptionType: server.dto.SubscriptionType
  nextBillingCycle: server.dto.BillingCycle
  discountUntil?: Date
  discountPercentage?: server.dto.Percentage
  enabledInternalUsers: number
}

const BillingCycleRow = (props: Props) => {
  const currentCycle = props.activeSubscription.billingCycle;
  const nextCycle = props.nextBillingCycle;

  switch (props.nextSubscriptionType.value) {
    case "free":
    case "readOnly":
      return <></>;
    case "paid":
      return <tr>
        <td>{RESX.Subscription.BillingCycle}:</td>
        <td>
          {findEnumLabel(currentCycle, serverReference.billingCycles)}
          {(currentCycle !== nextCycle.value) && <div className="s1_gray">{RESX.Subscription.ChangesToXOn.replace("{0}", nextCycle.text)} {formatLongDate(props.activeSubscription.end)}</div>}
        </td>
      </tr>;
    case "cancelledByClient":
    case "cancelledBySystemOne":
    case "trialEnd":
      return <tr>
        <td>{RESX.Subscription.resxSubscription}:</td>
        <td>{RESX.Subscription.Cancelled}</td>
      </tr >;
  }

  return <tr>
    <td>{RESX.GeneralWarnings.Warning}:</td>
    <td><div className="red">Error: subscription type {props.nextSubscriptionType.value} is not implemented. Contact SystemOne.</div>
    </td>
  </tr>;
};

interface SubscriptionDetailsProps extends Props {
  onChangeButtonClick?: () => void
}

const DiscountOnNextPaidRow = (props: Props) => {
  if ((props.discountPercentage === null) || props.discountPercentage.value <= 0 || props.nextSubscriptionType.value !== "paid") {
    return <></>;
  }

  if (props.discountUntil !== null && moment.utc(props.discountUntil) < moment.utc(props.activeSubscription.end)) {
    return <></>;
  }

  const showDiscountUntilText = () => (props.discountUntil != null);

  return (<div>
    {props.discountPercentage.text} {RESX.Subscription.Discount}
    {showDiscountUntilText() && <span className="text-sublabel"> ({RESX.DateTime.lblUntil} {formatShortDate(moment.utc(props.discountUntil))})</span>}
  </div>);
};

const NextIsAlsoPaidRow = (props: Props) => {
  if (props.nextSubscriptionType.value !== "paid") {
    return <></>;
  }

  const daysUntilEndText = () => (props.activeSubscription.daysUntilEnd === 1
    ? `${RESX.DateTime.YDay}`
    : `${props.activeSubscription.daysUntilEnd} ${RESX.DateTime.days}`
  );

  const forUsers = () => (props.enabledInternalUsers === 1
    ? RESX.Subscription.For1InternalUser
    : RESX.Subscription.ForXInternalUsers.replace("{0}", props.enabledInternalUsers.toString())
  );

  return <tr>
    <td>{RESX.Subscription.Renewal}:</td>
    <td>
      <div>
        {formatLongDate(props.activeSubscription.end)}
        <span className="text-sublabel"> ({RESX.DateTime.lblIn} {daysUntilEndText()})</span>
      </div>
      <div>{forUsers()}</div>
      <DiscountOnNextPaidRow {...props} />
    </td>
  </tr>;
};

const DetailsTable = (props: SubscriptionDetailsProps) => (<table className="s1_table clean rowspace-big margin-bottom-larger">
  <tbody>
    <tr>
      <td>{RESX.UserType.InternalUsers}:</td>
      <td className="flex">
        <div className="grow1">
          <UpToInternalUsers upTo={props.activeSubscription.internalUsers} inUse={props.enabledInternalUsers} />
        </div>
        <div>
          <SupportQuestionMark articleId={serverReference.supportArticleIds.usersInSubscription} />
        </div>
      </td>
    </tr>
    <BillingCycleRow {...props} />
    <NextIsAlsoPaidRow {...props} />
    <TermsRow />
  </tbody >
</table >);

const PaidSubscriptionDetails = (props: Props) => {
  switch (props.nextSubscriptionType.value) {
    case "free":
      return <>
        <DetailsTable {...props} />
        <NextIsFree {...props} />
      </>;
    case "paid":
      return <>
        <DetailsTable {...props} />
        <SaveByPayingPerYear {...props} />
      </>;
    case "readOnly":
      return <>
        <DetailsTable {...props} />
        <NextIsReadOnly {...props} />
      </>;
    case "cancelledByClient":
    case "cancelledBySystemOne":
    case "trialEnd":
      return <>
        <DetailsTable {...props} />
        <NextIsCancelled {...props} tip="readOnly" />
      </>;
    default:
      return <NotImplementedSubscription />;
  }
};

export default PaidSubscriptionDetails;
